<script>
  import Icon from "@iconify/svelte";
  import { t } from "../lib/i18n.svelte.js";
  import { api } from "../lib/api.svelte.js";
  import { push } from "svelte-spa-router";
  import { addToast } from "../components/Toast.svelte";
  import Layout from "../components/Layout.svelte";
  import Card from "../components/Card.svelte";
  import Skeleton from "../components/Skeleton.svelte";
  import Button from "../components/Button.svelte";
  import Input from "../components/Input.svelte";

  let { params = {} } = $props();

  let loading = $state(true);
  let saving = $state(false);
  let error = $state("");
  let site = $state(null);
  let confirmDelete = $state(false);
  let deleting = $state(false);

  // Edit form
  let editTitle = $state("");
  let editDescription = $state("");
  let editSlug = $state("");

  let siteId = $derived(params.id);

  $effect(() => {
    if (siteId) loadSite();
  });

  async function loadSite() {
    loading = true;
    error = "";
    try {
      site = await api.get(`/sites/${siteId}`);
      editTitle = site.title || "";
      editDescription = site.description || "";
      editSlug = site.slug || "";
    } catch (err) {
      error = err.message || t("common.error");
    } finally {
      loading = false;
    }
  }

  async function handleSave() {
    saving = true;
    try {
      await api.put(`/sites/${siteId}`, {
        title: editTitle,
        description: editDescription,
        slug: editSlug,
      });
      addToast(t("common.success"), "success");
      await loadSite();
    } catch (err) {
      addToast(err.message || t("common.error"), "error");
    } finally {
      saving = false;
    }
  }

  async function handleDelete() {
    deleting = true;
    try {
      await api.del(`/sites/${siteId}`);
      addToast(t("common.success"), "success");
      push("/sites");
    } catch (err) {
      addToast(err.message || t("common.error"), "error");
    } finally {
      deleting = false;
      confirmDelete = false;
    }
  }
</script>

<Layout>
  <div class="space-y-6">
    <!-- Back link -->
    <button
      type="button"
      class="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
      onclick={() => push("/sites")}
    >
      <Icon icon="mdi:arrow-left" class="w-4 h-4" />
      {t("common.back")}
    </button>

    {#if error}
      <div class="px-3 py-2 rounded border border-rose-500/30 bg-rose-500/10 text-sm text-rose-200">
        {error}
      </div>
    {/if}

    {#if loading}
      <Card><Skeleton lines={8} /></Card>
    {:else if site}
      <!-- Site info -->
      <Card>
        <div class="space-y-6">
          <!-- Screenshot -->
          {#if site.screenshot_url}
            <img
              src={site.screenshot_url}
              alt={site.title || site.slug}
              class="site-card-img w-full max-w-xl"
            />
          {:else}
            <div class="site-card-img w-full max-w-xl bg-white/[0.04] flex items-center justify-center">
              <Icon icon="mdi:web" class="w-12 h-12 text-zinc-600" />
            </div>
          {/if}

          <!-- Details -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span class="text-zinc-500">{t("common.title")}</span>
              <p class="text-white mt-0.5">{site.title || "-"}</p>
            </div>
            <div>
              <span class="text-zinc-500">{t("sites.slug")}</span>
              <p class="text-white mt-0.5">/{site.slug || "-"}</p>
            </div>
            <div class="sm:col-span-2">
              <span class="text-zinc-500">{t("common.description")}</span>
              <p class="text-white mt-0.5">{site.description || "-"}</p>
            </div>
            {#if site.storage_path}
              <div class="sm:col-span-2">
                <span class="text-zinc-500">{t("settings.storage")}</span>
                <p class="text-white mt-0.5 font-mono text-xs">{site.storage_path}</p>
              </div>
            {/if}
          </div>
        </div>
      </Card>

      <!-- Edit form -->
      <Card title={t("common.edit")}>
        <div class="space-y-4">
          <Input label={t("common.title")} bind:value={editTitle} />
          <Input label={t("sites.slug")} bind:value={editSlug} />
          <div class="space-y-1">
            <label class="block text-sm font-medium text-zinc-300">{t("common.description")}</label>
            <textarea
              bind:value={editDescription}
              rows="3"
              class="w-full px-4 py-3 bg-black/30 border border-white/10 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-400/20 focus:border-zinc-400/40 text-sm resize-y"
            ></textarea>
          </div>
          <div class="flex justify-end">
            <Button onclick={handleSave} loading={saving}>
              {t("common.save")}
            </Button>
          </div>
        </div>
      </Card>

      <!-- Danger zone -->
      <Card>
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 class="text-sm font-semibold text-rose-400">{t("common.delete")}</h3>
            <p class="text-xs text-zinc-500 mt-1">{t("sites.delete_warning")}</p>
          </div>
          {#if confirmDelete}
            <div class="flex items-center gap-2">
              <Button variant="secondary" onclick={() => (confirmDelete = false)}>{t("common.cancel")}</Button>
              <Button variant="danger" onclick={handleDelete} loading={deleting}>
                {t("common.confirm")}
              </Button>
            </div>
          {:else}
            <Button variant="danger" onclick={() => (confirmDelete = true)}>
              <Icon icon="mdi:delete" class="w-4 h-4" />
              {t("common.delete")}
            </Button>
          {/if}
        </div>
      </Card>
    {/if}
  </div>
</Layout>
